import { CONFIG } from '../config';

// Error types
export const ERROR_TYPES = {
  WALLET_NOT_CONNECTED: 'WALLET_NOT_CONNECTED',
  TRANSACTION_FAILED: 'TRANSACTION_FAILED',
  FILE_UPLOAD_FAILED: 'FILE_UPLOAD_FAILED',
  POLICY_NOT_FOUND: 'POLICY_NOT_FOUND',
  INVALID_INPUT: 'INVALID_INPUT',
  NETWORK_ERROR: 'NETWORK_ERROR',
  CONTRACT_ERROR: 'CONTRACT_ERROR',
  PINATA_ERROR: 'PINATA_ERROR'
};

// Custom error class
export class WillGuardError extends Error {
  constructor(type, message, details = null) {
    super(message);
    this.name = 'WillGuardError';
    this.type = type;
    this.details = details;
    this.timestamp = new Date().toISOString();
  }
}

// Error handler
export const handleError = (error, context = '') => {
  let errorType = ERROR_TYPES.NETWORK_ERROR;
  let userMessage = 'An unexpected error occurred';
  let technicalMessage = error.message;

  // Categorize errors
  if (error.message.includes('User denied')) {
    errorType = ERROR_TYPES.WALLET_NOT_CONNECTED;
    userMessage = 'Transaction was cancelled by user';
  } else if (error.message.includes('insufficient funds')) {
    errorType = ERROR_TYPES.TRANSACTION_FAILED;
    userMessage = 'Insufficient funds for transaction';
  } else if (error.message.includes('Policy not found')) {
    errorType = ERROR_TYPES.POLICY_NOT_FOUND;
    userMessage = 'Policy not found or invalid Policy ID';
  } else if (error.message.includes('Pinata')) {
    errorType = ERROR_TYPES.PINATA_ERROR;
    userMessage = 'File upload failed. Please try again.';
  } else if (error.message.includes('MetaMask')) {
    errorType = ERROR_TYPES.WALLET_NOT_CONNECTED;
    userMessage = 'Please install and connect MetaMask';
  }

  // Log error in development
  if (CONFIG.ENABLE_DEBUG) {
    console.error(`🚨 Error in ${context}:`, {
      type: errorType,
      message: technicalMessage,
      userMessage,
      stack: error.stack,
      timestamp: new Date().toISOString()
    });
  }

  return {
    type: errorType,
    userMessage,
    technicalMessage,
    context
  };
};

// Input validation
export const validateInput = {
  policyId: (policyId) => {
    if (!policyId || typeof policyId !== 'string') {
      throw new WillGuardError(ERROR_TYPES.INVALID_INPUT, 'Policy ID is required');
    }
    if (policyId.length !== 66 || !policyId.startsWith('0x')) {
      throw new WillGuardError(ERROR_TYPES.INVALID_INPUT, 'Invalid Policy ID format');
    }
    return true;
  },

  file: (file) => {
    if (!file) {
      throw new WillGuardError(ERROR_TYPES.INVALID_INPUT, 'Please select a file to upload');
    }
    if (file.size > CONFIG.MAX_FILE_SIZE) {
      throw new WillGuardError(ERROR_TYPES.INVALID_INPUT, `File size must be less than ${CONFIG.MAX_FILE_SIZE / (1024 * 1024)}MB`);
    }
    const extension = file.name.split('.').pop().toLowerCase();
    if (!CONFIG.SUPPORTED_FILE_TYPES.includes(extension)) {
      throw new WillGuardError(ERROR_TYPES.INVALID_INPUT, `File type not supported. Supported types: ${CONFIG.SUPPORTED_FILE_TYPES.join(', ')}`);
    }
    return true;
  },

  heartbeatInterval: (interval) => {
    const num = Number(interval);
    if (isNaN(num) || num <= 0) {
      throw new WillGuardError(ERROR_TYPES.INVALID_INPUT, 'Heartbeat interval must be a positive number');
    }
    if (num < 3600) { // Less than 1 hour
      throw new WillGuardError(ERROR_TYPES.INVALID_INPUT, 'Heartbeat interval must be at least 1 hour (3600 seconds)');
    }
    return true;
  },

  quorum: (quorum) => {
    const num = Number(quorum);
    if (isNaN(num) || num < 1) {
      throw new WillGuardError(ERROR_TYPES.INVALID_INPUT, 'Quorum must be at least 1');
    }
    return true;
  },

  address: (address) => {
    if (!address || typeof address !== 'string') {
      throw new WillGuardError(ERROR_TYPES.INVALID_INPUT, 'Address is required');
    }
    if (!/^0x[a-fA-F0-9]{40}$/.test(address)) {
      throw new WillGuardError(ERROR_TYPES.INVALID_INPUT, 'Invalid Ethereum address format');
    }
    return true;
  }
};

// Success handler
export const handleSuccess = (message, data = null) => {
  if (CONFIG.ENABLE_DEBUG) {
    console.log('✅ Success:', message, data);
  }
  return { success: true, message, data };
};

// Loading state helper
export const createLoadingState = () => ({
  isLoading: false,
  error: null,
  success: null
});

// Async wrapper for error handling
export const withErrorHandling = async (asyncFn, context = '') => {
  try {
    return await asyncFn();
  } catch (error) {
    return handleError(error, context);
  }
};
